import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ExternalLink, Filter, Zap, Loader2, Sparkles } from "lucide-react";

export default function AdminScrapersCuradoria() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brandFilter, setBrandFilter] = useState("");
    const [brands, setBrands] = useState([]);
    const [selectedCat, setSelectedCat] = useState({});
    const [loading, setLoading] = useState(true);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkRequireImage, setBulkRequireImage] = useState(true);
    const [bulkRequireCategory, setBulkRequireCategory] = useState(true);
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiPreview, setAiPreview] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        const params = brandFilter ? { brand_id: brandFilter } : {};
        const [pending, cats, brs] = await Promise.all([
            api.get("/admin/scrapers/pending", { params }),
            api.get("/admin/categories"),
            api.get("/admin/brands"),
        ]);
        setItems(pending.data);
        setCategories(cats.data);
        setBrands(brs.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, [brandFilter]);

    const approve = async (item) => {
        try {
            await api.post(`/admin/scrapers/pending/${item.id}/approve`, {
                category_id: selectedCat[item.id] || null,
            });
            toast.success("Aprovado e publicado no catálogo");
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const reject = async (item) => {
        try {
            await api.delete(`/admin/scrapers/pending/${item.id}`);
            toast.success("Rejeitado");
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const openBulk = async () => {
        setBulkOpen(true);
        setBulkPreview(null);
    };

    const runBulkPreview = async () => {
        setBulkBusy(true);
        try {
            const { data } = await api.post("/admin/scrapers/pending/bulk-approve", {
                brand_id: brandFilter || null,
                require_image: bulkRequireImage,
                require_category: bulkRequireCategory,
                dry_run: true,
            });
            setBulkPreview(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na simulação");
        } finally { setBulkBusy(false); }
    };

    const runBulkExecute = async () => {
        setBulkBusy(true);
        try {
            const { data } = await api.post("/admin/scrapers/pending/bulk-approve", {
                brand_id: brandFilter || null,
                require_image: bulkRequireImage,
                require_category: bulkRequireCategory,
                dry_run: false,
            });
            toast.success(`${data.approved} aprovados · ${data.skipped_no_image} sem foto · ${data.skipped_no_category} sem categoria`);
            setBulkOpen(false);
            setBulkPreview(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na aprovação em massa");
        } finally { setBulkBusy(false); }
    };

    const runAiPreview = async () => {
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/scrapers/pending/ai-classify", {
                require_image: true,
                dry_run: true,
            });
            setAiPreview(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na classificação IA");
        } finally { setAiBusy(false); }
    };

    const runAiExecute = async () => {
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/scrapers/pending/ai-classify", {
                require_image: true,
                dry_run: false,
            });
            toast.success(`${data.approved} classificados e aprovados pela IA · ${data.no_match} sem match`);
            setAiOpen(false);
            setAiPreview(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na execução IA");
        } finally { setAiBusy(false); }
    };

    return (
        <div className="p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Fase 8 · Curadoria</div>
                    <h1 className="mt-1 font-heading font-black text-3xl tracking-tight">Produtos aguardando aprovação</h1>
                    <p className="mt-2 text-sm text-neutral-500">{items.length} item(ns) pendentes · aprove para publicar no catálogo público.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={openBulk}
                        data-testid="bulk-approve-open"
                        disabled={items.length === 0}
                        className="h-11 px-4 bg-brand-yellow text-ink font-heading font-bold uppercase tracking-wider text-xs hover:bg-brand-yellow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Zap size={14} /> Aprovar automaticamente
                    </button>
                    <button
                        onClick={() => { setAiOpen(true); setAiPreview(null); }}
                        data-testid="ai-classify-open"
                        disabled={items.length === 0}
                        className="h-11 px-4 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-xs hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Sparkles size={14} /> Classificar com IA
                    </button>
                    <Filter size={14} className="text-neutral-500" />
                    <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="h-11 px-4 bg-white border border-neutral-300 text-sm" data-testid="brand-filter">
                        <option value="">Todas as marcas</option>
                        {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="mt-8 font-mono text-sm text-neutral-500">carregando…</div>
            ) : items.length === 0 ? (
                <div className="mt-8 border border-dashed border-neutral-300 bg-white p-16 text-center">
                    <div className="font-mono text-xs uppercase tracking-widest text-neutral-400">// fila vazia</div>
                    <p className="mt-3 text-neutral-600">Nenhum produto aguardando curadoria.</p>
                </div>
            ) : (
                <div className="mt-6 grid gap-4">
                    {items.map((item) => (
                        <div key={item.id} className="bg-white border border-neutral-300 p-5 grid grid-cols-[128px,1fr,auto] gap-5" data-testid={`staged-${item.id}`}>
                            <div className="w-32 h-32 bg-neutral-100 border border-neutral-200 overflow-hidden">
                                {item.image_url ? (
                                    <img src={item.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                ) : <div className="w-full h-full grid place-items-center font-mono text-xs text-neutral-400">sem imagem</div>}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                                    <span className="text-brand-blue font-bold">{item.brand_name}</span>
                                    {item.sku && <span>· SKU {item.sku}</span>}
                                    {item.category_hint && <span>· sugerido: {item.category_hint}</span>}
                                </div>
                                <div className="mt-2 font-heading font-bold text-lg leading-snug">{item.name}</div>
                                {item.description && <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{item.description}</p>}
                                {Object.keys(item.specs || {}).length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {Object.entries(item.specs).slice(0, 5).map(([k, v]) => (
                                            <span key={k} className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 font-mono text-[10px]">
                                                <span className="text-neutral-500 uppercase tracking-widest">{k}:</span>
                                                <span className="text-neutral-800">{v}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {item.product_url && (
                                    <a href={item.product_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-brand-blue text-xs font-mono hover:underline">
                                        <ExternalLink size={11} /> ver na origem
                                    </a>
                                )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <select
                                    value={selectedCat[item.id] || ""}
                                    onChange={(e) => setSelectedCat({ ...selectedCat, [item.id]: e.target.value })}
                                    className="h-10 px-3 bg-white border border-neutral-300 text-sm w-56"
                                    data-testid={`cat-select-${item.id}`}
                                >
                                    <option value="">— categoria —</option>
                                    {categories.map((c) => <option key={c.id} value={c.id}>{String(c.order || 0).padStart(2, "0")} · {c.name}</option>)}
                                </select>
                                <div className="flex gap-2">
                                    <button data-testid={`reject-${item.id}`} onClick={() => reject(item)} className="h-10 px-3 bg-white border border-neutral-300 hover:bg-red-50 hover:border-red-300 text-red-700 text-sm font-medium flex items-center gap-1">
                                        <X size={14} /> Rejeitar
                                    </button>
                                    <button data-testid={`approve-${item.id}`} onClick={() => approve(item)} className="h-10 px-4 bg-brand-blue text-white text-sm font-heading font-bold uppercase tracking-wider hover:bg-brand-blue-hover flex items-center gap-1">
                                        <Check size={14} /> Aprovar
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* BULK MODAL */}
            {bulkOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="bulk-modal">
                    <div className="bg-white max-w-lg w-full border-2 border-brand-yellow">
                        <div className="p-6 border-b border-neutral-200">
                            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-brand-blue">
                                <Zap size={12} /> Aprovação automática
                            </div>
                            <h2 className="mt-1 font-heading font-black text-2xl tracking-tight">Publicar em massa</h2>
                            <p className="mt-2 text-sm text-neutral-600">
                                Aprova todos os itens pendentes {brandFilter ? "da marca filtrada" : "de todas as marcas"} com auto-categorização por palavras-chave. Recomendado: rodar simulação primeiro.
                            </p>
                        </div>

                        <div className="p-6 space-y-3">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={bulkRequireImage} onChange={(e) => { setBulkRequireImage(e.target.checked); setBulkPreview(null); }} className="mt-1" data-testid="bulk-require-image" />
                                <div>
                                    <div className="text-sm font-heading font-bold">Apenas com foto real cadastrada</div>
                                    <div className="text-xs text-neutral-500">Recomendado — só aprova itens que já têm image_url válido do fabricante.</div>
                                </div>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input type="checkbox" checked={bulkRequireCategory} onChange={(e) => { setBulkRequireCategory(e.target.checked); setBulkPreview(null); }} className="mt-1" data-testid="bulk-require-category" />
                                <div>
                                    <div className="text-sm font-heading font-bold">Apenas com categoria auto-identificada</div>
                                    <div className="text-xs text-neutral-500">Recomendado — pula itens que o classificador não conseguiu enquadrar em nenhuma das 24 categorias.</div>
                                </div>
                            </label>
                        </div>

                        {bulkPreview && (
                            <div className="mx-6 mb-4 border border-neutral-300 bg-neutral-50 p-4 font-mono text-xs">
                                <div className="uppercase tracking-widest text-neutral-500 mb-2">// prévia (dry-run)</div>
                                <div>&gt; total pendente: <span className="text-neutral-900 font-bold">{bulkPreview.total_pending}</span></div>
                                <div>&gt; serão aprovados: <span className="text-green-700 font-bold" data-testid="bulk-preview-approved">{bulkPreview.approved}</span></div>
                                <div>&gt; pulados (sem foto): <span className="text-neutral-700">{bulkPreview.skipped_no_image}</span></div>
                                <div>&gt; pulados (sem categoria): <span className="text-neutral-700">{bulkPreview.skipped_no_category}</span></div>
                                {bulkPreview.sample && bulkPreview.sample.length > 0 && (
                                    <div className="mt-3">
                                        <div className="uppercase tracking-widest text-neutral-500 mb-1">// amostra</div>
                                        {bulkPreview.sample.map((s, i) => (
                                            <div key={i} className="text-[10px] text-neutral-700 truncate">• {s.brand} · {s.name} → <span className="text-brand-blue">{s.category || "?"}</span></div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="p-6 border-t border-neutral-200 flex items-center justify-between gap-2">
                            <button onClick={() => { setBulkOpen(false); setBulkPreview(null); }} className="h-11 px-4 border border-neutral-300 text-sm hover:bg-neutral-50" data-testid="bulk-cancel">
                                Cancelar
                            </button>
                            <div className="flex gap-2">
                                <button onClick={runBulkPreview} disabled={bulkBusy} className="h-11 px-4 border border-brand-blue text-brand-blue text-sm font-heading font-bold uppercase tracking-wider hover:bg-brand-blue/5 disabled:opacity-50 flex items-center gap-2" data-testid="bulk-preview">
                                    {bulkBusy && !bulkPreview ? <Loader2 size={14} className="animate-spin" /> : null}
                                    Simular
                                </button>
                                <button
                                    onClick={runBulkExecute}
                                    disabled={bulkBusy || !bulkPreview || bulkPreview.approved === 0}
                                    className="h-11 px-4 bg-brand-blue text-white text-sm font-heading font-bold uppercase tracking-wider hover:bg-brand-blue-hover disabled:opacity-50 flex items-center gap-2"
                                    data-testid="bulk-execute"
                                >
                                    {bulkBusy && bulkPreview ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                                    Aprovar {bulkPreview?.approved || 0}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI CLASSIFY MODAL */}
            {aiOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="ai-modal">
                    <div className="bg-white max-w-lg w-full border-2 border-brand-blue">
                        <div className="p-6 border-b border-neutral-200">
                            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-brand-blue">
                                <Sparkles size={12} /> Classificação com IA (Gemini)
                            </div>
                            <h2 className="mt-1 font-heading font-black text-2xl tracking-tight">Segunda passada com IA</h2>
                            <p className="mt-2 text-sm text-neutral-600">
                                Envia os pendentes <strong>com foto real</strong> ao Gemini 3 Flash para classificar em uma das 24 categorias reais. Recomendado para itens que a aprovação automática não conseguiu enquadrar por palavras-chave.
                            </p>
                            <p className="mt-2 text-[11px] font-mono text-neutral-500">Só aprova quem tiver foto real cadastrada. Cada item aprovado é marcado como <code>ai_classified: true</code>.</p>
                        </div>

                        {aiPreview && (
                            <div className="mx-6 mb-4 border border-neutral-300 bg-neutral-50 p-4 font-mono text-xs">
                                <div className="uppercase tracking-widest text-neutral-500 mb-2">// prévia IA (dry-run)</div>
                                <div>&gt; total analisado: <span className="text-neutral-900 font-bold">{aiPreview.total_pending}</span></div>
                                <div>&gt; classificados pela IA: <span className="text-green-700 font-bold" data-testid="ai-preview-approved">{aiPreview.classified}</span></div>
                                <div>&gt; sem match: <span className="text-neutral-700">{aiPreview.no_match}</span></div>
                                {aiPreview.sample && aiPreview.sample.length > 0 && (
                                    <div className="mt-3">
                                        <div className="uppercase tracking-widest text-neutral-500 mb-1">// amostra da IA</div>
                                        {aiPreview.sample.map((s, i) => (
                                            <div key={i} className="text-[10px] text-neutral-700 truncate">• {s.brand} · {s.name} → <span className="text-brand-blue">{s.category}</span></div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="p-6 border-t border-neutral-200 flex items-center justify-between gap-2">
                            <button onClick={() => { setAiOpen(false); setAiPreview(null); }} className="h-11 px-4 border border-neutral-300 text-sm hover:bg-neutral-50" data-testid="ai-cancel">
                                Cancelar
                            </button>
                            <div className="flex gap-2">
                                <button onClick={runAiPreview} disabled={aiBusy} className="h-11 px-4 border border-brand-blue text-brand-blue text-sm font-heading font-bold uppercase tracking-wider hover:bg-brand-blue/5 disabled:opacity-50 flex items-center gap-2" data-testid="ai-preview">
                                    {aiBusy && !aiPreview ? <Loader2 size={14} className="animate-spin" /> : null}
                                    Simular IA
                                </button>
                                <button
                                    onClick={runAiExecute}
                                    disabled={aiBusy || !aiPreview || aiPreview.classified === 0}
                                    className="h-11 px-4 bg-brand-blue text-white text-sm font-heading font-bold uppercase tracking-wider hover:bg-brand-blue-hover disabled:opacity-50 flex items-center gap-2"
                                    data-testid="ai-execute"
                                >
                                    {aiBusy && aiPreview ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                    Aprovar {aiPreview?.classified || 0}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
